import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// CM6 core
import { EditorState, Compartment, StateField, StateEffect, Extension } from '@codemirror/state';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  highlightActiveLineGutter,
  dropCursor,
  hoverTooltip,
  Tooltip,
  ViewUpdate,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentMore,
  indentLess,
  toggleComment,
} from '@codemirror/commands';
import { searchKeymap } from '@codemirror/search';
import {
  autocompletion,
  completionKeymap,
  CompletionContext,
  CompletionResult,
  startCompletion,
} from '@codemirror/autocomplete';
import {
  bracketMatching,
  foldGutter,
  indentOnInput,
  syntaxHighlighting,
  defaultHighlightStyle,
  foldKeymap,
  LanguageSupport,
  StreamLanguage,
} from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { linter, lintGutter, setDiagnosticsEffect } from '@codemirror/lint';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { sql } from '@codemirror/lang-sql';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';

import { ApiReferenceService } from '../../services/api-reference/api-reference.service';
import { AutocompleteItem, ParamHint } from '../../services/api-reference/api-reference.models';
import { ConnectionService } from '../../services/connection/connection.service';
import { PushDestinationService } from '../../services/push-destination/push-destination.service';
import { StepService } from '../../services/step/step.service';
import { NotebookFilesService } from '../../services/notebook-files/notebook-files.service';
import { ExecutionPlanService } from '../../services/execution-plan/execution-plan.service';
import { AppStateService } from '../../services/app-state/app-state.service';
import { ProjectStateService } from '../../services/project-state/project-state.service';
import { LlmService } from '../../services/llm/llm.service';
import { CodeLintService } from '../../services/code-lint/code-lint.service';

const EDITOR_PADDING = 10;
const PYTHON_INDENT_SIZE = 4;
const DEFAULT_INDENT_SIZE = 2;
const MODE_PYTHON = 'python';
const MIN_WORD_LENGTH_FOR_AUTOCOMPLETE = 3;

function normalizeLineEndings(str: string): string {
  if (!str) {
    return str;
  }
  return str.replace(/\r\n|\r/g, '\n');
}

// ── Fullscreen state field ──────────────────────────────────────────────────
const toggleFullscreenEffect = StateEffect.define<boolean>();

const fullscreenField = StateField.define<boolean>({
  create() {
    return false;
  },
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(toggleFullscreenEffect)) {
        return e.value;
      }
    }
    return value;
  },
});

// ── Eclipse-like light theme ────────────────────────────────────────────────
const eclipseTheme = EditorView.theme({
  '&': {
    backgroundColor: '#ffffff',
    color: '#000000',
  },
  '.cm-content': {
    caretColor: '#000000',
  },
  '.cm-cursor': {
    borderLeftColor: '#000000',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': {
    backgroundColor: '#d7d4f0',
  },
  '.cm-activeLine': {
    backgroundColor: '#e8f2fe',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#e8f2fe',
  },
  '.cm-gutters': {
    backgroundColor: '#f7f7f7',
    color: '#999999',
    border: 'none',
  },
});

@Component({
  standalone: true,
  selector: 'app-ngx-codemirror',
  template: `<div #editorContainer class="cm-host"></div>`,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    .cm-host {
      width: 100%;
    }
    :host ::ng-deep .cm-editor {
      border: 1px solid rgba(0, 0, 0, 0.12);
      width: 100%;
      outline: none;
    }
    :host ::ng-deep .cm-editor.cm-focused {
      outline: none;
    }
    :host ::ng-deep .cm-editor.cm-fullscreen {
      position: fixed !important;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999;
      height: 100vh !important;
      width: 100vw !important;
      border: none !important;
      border-radius: 0 !important;
      background: var(--cm-bg, white);
    }
    :host ::ng-deep .cm-scroller {
      overflow: auto;
    }
    :host ::ng-deep .cm-api-detail {
      padding: 8px 12px;
      font-size: 13px;
      max-width: 400px;
    }
    :host ::ng-deep .cm-api-detail .detail-header {
      font-weight: 600;
      color: var(--cm-detail-header, #4ec9b0);
      margin-bottom: 4px;
    }
    :host ::ng-deep .cm-api-detail .detail-description {
      color: var(--cm-detail-text, #666);
      margin-bottom: 8px;
    }
    :host ::ng-deep .cm-api-detail .detail-signature code {
      background: var(--cm-detail-code-bg, #f3f3f3);
      padding: 4px 8px;
      border-radius: 3px;
      display: block;
      font-family: monospace;
      font-size: 12px;
    }
    :host ::ng-deep .cm-api-detail .params-title {
      font-weight: 500;
      margin-top: 8px;
      margin-bottom: 4px;
    }
    :host ::ng-deep .cm-api-detail .param-row {
      display: flex;
      gap: 8px;
      font-size: 12px;
      padding: 2px 0;
    }
    :host ::ng-deep .cm-api-detail .param-name { font-weight: 500; }
    :host ::ng-deep .cm-api-detail .param-type { color: var(--cm-detail-type, #569cd6); }
    :host ::ng-deep .cm-api-detail .param-optional { color: var(--cm-detail-muted, #999); font-style: italic; }
    :host ::ng-deep .cm-api-detail .detail-returns { margin-top: 8px; font-size: 12px; }
    :host ::ng-deep .cm-api-detail .detail-availability { margin-top: 4px; font-size: 11px; color: var(--cm-detail-muted, #999); }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodemirrorComponent),
      multi: true,
    },
  ],
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodemirrorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  private readonly apiReferenceService = inject(ApiReferenceService);
  private readonly connectionService = inject(ConnectionService);
  private readonly pushDestinationService = inject(PushDestinationService);
  private readonly stepService = inject(StepService);
  private readonly notebookFilesService = inject(NotebookFilesService);
  private readonly executionPlanService = inject(ExecutionPlanService);
  private readonly appStateService = inject(AppStateService);
  private readonly projectStateService = inject(ProjectStateService);
  private readonly llmService = inject(LlmService);
  private readonly codeLintService = inject(CodeLintService);
  private readonly platformId = inject(PLATFORM_ID);

  // Cached hint data for synchronous autocomplete resolution
  private cachedFileNames: string[] = [];
  private cachedRoutineNames: string[] = [];
  private cachedStateKeys: string[] = [];
  private cachedLlmModels: { label: string; detail?: string }[] = [];
  private cachedPushDestinations: { label: string; detail?: string }[] = [];
  private fileNamesFetched = false;
  private routineNamesFetched = false;
  private stateKeysFetched = false;
  private llmModelsFetched = false;
  private pushDestinationsFetched = false;

  @Input() className: string = '';
  @Input() name: string = 'codemirror';
  @Input() autoFocus: boolean = false;

  private _options: Record<string, any> = {};
  private _prevOptions: Record<string, any> = {};

  /**
   * Options are stored as a SNAPSHOT COPY, never by reference.
   *
   * The copy is what makes {@link applyOptions} able to tell what changed: a
   * caller that mutates its own options object in place cannot silently alter
   * our `_options` too, so `_prevOptions` still holds the previous values when
   * the reconfigure runs.
   *
   * This used to be a KeyValueDiffer consulted from ngDoCheck. That relied on
   * Angular checking this OnPush component on every parent update, which
   * Angular 22 no longer does -- so an in-place mutation stopped reconfiguring
   * the editor at all and the step-type switch regressed (EHQ-149/EHQ-171).
   * Mutating callers now call applyOptions() explicitly instead.
   */
  @Input()
  set options(value: Record<string, any>) {
    // Same work as an explicit applyOptions() call — a new object reference
    // reaching the setter and an in-place mutation followed by applyOptions()
    // must stay indistinguishable, so there is only one implementation.
    this.applyOptions(value);
  }

  /**
   * Reconfigure the live editor from `next`.
   *
   * Call this after mutating the object bound to [options] in place; a
   * same-reference mutation cannot trigger the setter. No-ops safely before the
   * editor exists, since createEditor() builds from the current options.
   */
  applyOptions(next: Record<string, any>): void {
    this._prevOptions = { ...this._options };
    this._options = { ...next };
    if (this._view) {
      this.applyOptionChanges();
    }
  }

  minimumHeight = input(300);
  // 0 = unbounded. When > 0, caps the editor's DOM height so its internal
  // .cm-scroller handles overflow instead of growing past its container.
  maximumHeight = input(0);
  // Re-apply the height whenever the input bounds change (e.g. the host caps
  // it on window:resize) so the cap takes effect without waiting for the next
  // doc edit. Safe before createEditor — updateEditorHeight no-ops without a view.
  private readonly _heightBoundsEffect = effect(() => {
    this.minimumHeight();
    this.maximumHeight();
    this.updateEditorHeight();
  });
  @Input() preserveScrollPosition = false;

  @Output() cursorActivity = new EventEmitter<EditorView>();
  @Output() focusChange = new EventEmitter<boolean>();
  @Output() editorScroll = new EventEmitter<{ left: number; top: number }>();
  @Output() editorDrop = new EventEmitter<[EditorView, DragEvent]>();
  @Output() codeMirrorLoaded = new EventEmitter<EditorView>();

  @ViewChild('editorContainer', { static: true }) editorContainerRef!: ElementRef<HTMLDivElement>;

  value = '';
  disabled = false;
  isFocused = signal(false);

  private _view?: EditorView;

  // Compartments for runtime reconfiguration
  private languageCompartment = new Compartment();
  private readOnlyCompartment = new Compartment();
  private themeCompartment = new Compartment();
  private lineNumbersCompartment = new Compartment();
  private lineWrappingCompartment = new Compartment();
  private tabSizeCompartment = new Compartment();
  private lintCompartment = new Compartment();

  /** Public accessor for the EditorView instance */
  get editorView(): EditorView | undefined {
    return this._view;
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────
  private onChange = (_: any) => {};
  private onTouched = () => {};

  writeValue(value: string) {
    if (value === null || value === undefined) {
      return;
    }
    const normalizedValue = normalizeLineEndings(value);
    if (!this._view) {
      this.value = normalizedValue;
      return;
    }
    const cur = this._view.state.doc.toString();
    if (normalizedValue !== cur) {
      this.value = normalizedValue;
      const scrollPos = this.preserveScrollPosition
        ? { top: this._view.scrollDOM.scrollTop, left: this._view.scrollDOM.scrollLeft }
        : null;

      this._view.dispatch({
        changes: { from: 0, to: this._view.state.doc.length, insert: this.value },
      });

      if (scrollPos) {
        this._view.scrollDOM.scrollTop = scrollPos.top;
        this._view.scrollDOM.scrollLeft = scrollPos.left;
      }
      this.updateEditorHeight();
    }
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if (this._view) {
      this._view.dispatch({
        effects: this.readOnlyCompartment.reconfigure(
          EditorState.readOnly.of(this._options['readOnly'] === true || isDisabled)
        ),
      });
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────
  ngAfterViewInit() {
    this.createEditor();
  }

  ngOnDestroy() {
    this._view?.destroy();
  }

  // ── Editor creation ─────────────────────────────────────────────────────
  private createEditor(): void {
    const indentSize = this._options['mode'] === MODE_PYTHON ? PYTHON_INDENT_SIZE : DEFAULT_INDENT_SIZE;

    const extensions = [
      // History (undo/redo)
      history(),

      // Line numbers (compartment for runtime toggle)
      this.lineNumbersCompartment.of(
        this._options['lineNumbers'] !== false ? lineNumbers() : []
      ),

      // Line wrapping
      this.lineWrappingCompartment.of(
        this._options['lineWrapping'] ? EditorView.lineWrapping : []
      ),

      // Fold gutter
      ...(this._options['foldGutter'] !== false ? [foldGutter()] : []),

      // Bracket matching
      ...(this._options['matchBrackets'] !== false ? [bracketMatching()] : []),

      // Auto close brackets
      ...(this._options['autoCloseBrackets'] !== false ? [closeBrackets()] : []),

      // Active line highlight
      highlightActiveLine(),
      highlightActiveLineGutter(),

      // Drop cursor indicator for drag-and-drop
      dropCursor(),

      // Indent on input
      indentOnInput(),

      // Syntax highlighting
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),

      // Tab size
      this.tabSizeCompartment.of(EditorState.tabSize.of(indentSize)),

      // Language support (compartment)
      this.languageCompartment.of(this.getLanguageExtension(this._options['mode'])),

      // Lint gutter (markers) + lint source per mode (compartment). (EHQ-108)
      lintGutter(),
      this.lintCompartment.of(this.getLintExtension(this._options['mode'])),

      // Read-only (compartment)
      this.readOnlyCompartment.of(
        EditorState.readOnly.of(this._options['readOnly'] === true || this.disabled)
      ),

      // Theme (compartment)
      this.themeCompartment.of(this.getThemeExtension(this._options['theme'])),

      // Autocomplete
      autocompletion({
        override: [this.autocompleteSource.bind(this)],
        activateOnTyping: true,
      }),

      // Hover tooltip for API method documentation
      hoverTooltip(this.apiHoverTooltip.bind(this), { hoverTime: 300 }),

      // Fullscreen state field
      fullscreenField,

      // Key bindings
      keymap.of([
        // Ctrl/Cmd+Space → trigger autocomplete
        {
          key: 'Ctrl-Space',
          run: (view) => { startCompletion(view); return true; },
        },
        {
          key: 'Cmd-Space',
          // Note: Cmd-Space is typically captured by macOS Spotlight;
          // included for completeness
          run: (view) => { startCompletion(view); return true; },
        },
        // Tab → smart indent (spaces, not tabs)
        {
          key: 'Tab',
          run: (view) => {
            if (view.state.selection.main.empty) {
              // Read tabSize from editor state so it stays current after mode changes
              const tabSize = view.state.facet(EditorState.tabSize);
              const spaces = ' '.repeat(tabSize);
              view.dispatch(view.state.replaceSelection(spaces));
              return true;
            }
            indentMore(view);
            return true;
          },
          shift: (view) => { indentLess(view); return true; },
        },
        // F11 / Cmd-Enter → fullscreen toggle
        {
          key: 'F11',
          run: (view) => { this.toggleFullscreen(view); return true; },
        },
        {
          key: 'Cmd-Enter',
          run: (view) => { this.toggleFullscreen(view); return true; },
        },
        // Esc → exit fullscreen
        {
          key: 'Escape',
          run: (view) => {
            if (view.state.field(fullscreenField)) {
              this.toggleFullscreen(view, false);
              return true;
            }
            return false;
          },
        },
        // Ctrl+/ / Cmd+/ → toggle comment
        {
          key: 'Mod-/',
          run: (view) => { toggleComment(view); return true; },
        },
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
      ]),

      // Update listener for value changes, cursor, focus, scroll
      EditorView.updateListener.of((update: ViewUpdate) => {
        // Document changed
        if (update.docChanged) {
          const newVal = normalizeLineEndings(update.state.doc.toString());
          if (this.value !== newVal) {
            this.value = newVal;
            this.onChange(this.value);
          }
          this.updateEditorHeight();
        }
        // Cursor moved
        if (update.selectionSet) {
          this.cursorActivity.emit(this._view!);
        }
        // Focus changed
        if (update.focusChanged) {
          const focused = update.view.hasFocus;
          if (this.isFocused() !== focused) {
            this.isFocused.set(focused);
            this.focusChange.emit(focused);
            if (!focused) {
              this.onTouched();
            }
          }
        }
      }),

      // Scroll listener
      EditorView.domEventHandlers({
        scroll: (_event, view) => {
          this.editorScroll.emit({
            left: view.scrollDOM.scrollLeft,
            top: view.scrollDOM.scrollTop,
          });
          return false;
        },
        drop: (event, view) => {
          this.editorDrop.emit([view, event]);
          return false;
        },
      }),
    ];

    const state = EditorState.create({
      doc: this.value,
      extensions,
    });

    this._view = new EditorView({
      state,
      parent: this.editorContainerRef.nativeElement,
    });

    this.updateEditorHeight();

    if (this.autoFocus) {
      this._view.focus();
    }

    this.codeMirrorLoaded.emit(this._view);
  }

  // ── Language mapping ────────────────────────────────────────────────────
  private getLanguageExtension(mode: string | undefined): LanguageSupport | [] {
    switch (mode) {
      case 'javascript':
        return javascript();
      // JSON reuses the JavaScript language support for highlighting and
      // bracket handling, but is a distinct mode so it opts out of JS linting —
      // a bare JSON object parses as a labelled block and ESLint reports a
      // fatal "Unexpected token :" on perfectly valid content. (EHQ-108)
      case 'json':
        return javascript();
      case 'python':
        return python();
      case 'sql':
        return sql();
      case 'markdown':
        return markdown();
      case 'rhai':
        // No CodeMirror Rhai mode exists. Rust's is close enough to highlight it
        // (EHQ-76); drop it if it misleads. Lazy, like stex.
        this.loadRustMode();
        return [] as any;
      case 'stex':
        // Defer stex loading — use dynamic import to avoid bundling legacy modes always
        // For now, return empty and load lazily
        this.loadStexMode();
        return [] as any;
      default:
        return [] as any;
    }
  }

  private async loadStexMode(): Promise<void> {
    try {
      const { stex } = await import('@codemirror/legacy-modes/mode/stex');
      if (this._view) {
        this._view.dispatch({
          effects: this.languageCompartment.reconfigure(
            new LanguageSupport(StreamLanguage.define(stex))
          ),
        });
      }
    } catch {
      // stex mode not available — remain as plain text
    }
  }

  private async loadRustMode(): Promise<void> {
    try {
      const { rust } = await import('@codemirror/legacy-modes/mode/rust');
      if (this._view) {
        this._view.dispatch({
          effects: this.languageCompartment.reconfigure(
            new LanguageSupport(StreamLanguage.define(rust))
          ),
        });
      }
    } catch {
      // rust mode not available — remain as plain text
    }
  }

  // ── Lint mapping ──────────────────────────────────────────────────────
  // Returns a CM lint extension for modes that support it, else nothing.
  // JS is synchronous (ESLint); Python is async (pyflakes in Pyodide) — the
  // linter() default debounce (~750ms, on doc change) keeps the Pyodide
  // round-trip from firing on every keystroke. (EHQ-108)
  //
  // Note 'json' is deliberately absent: those editors hold data, not code, so
  // they get JS highlighting without JS diagnostics. (EHQ-108)
  private getLintExtension(mode: string | undefined): Extension {
    switch (mode) {
      case 'javascript':
        return linter(this.codeLintService.jsLintSource);
      case 'python':
        return linter(this.codeLintService.pythonLintSource);
      default:
        return [];
    }
  }

  // ── Theme mapping ─────────────────────────────────────────────────────
  private getThemeExtension(theme: string | undefined): any[] {
    switch (theme) {
      case 'eclipse':
        return [eclipseTheme];
      case 'monokai':
      case 'dark':
        return [oneDark];
      default:
        return [eclipseTheme];
    }
  }

  // ── Fullscreen toggle ─────────────────────────────────────────────────
  private toggleFullscreen(view: EditorView, force?: boolean): void {
    const current = view.state.field(fullscreenField);
    const next = force !== undefined ? force : !current;
    view.dispatch({ effects: toggleFullscreenEffect.of(next) });

    if (next) {
      view.dom.classList.add('cm-fullscreen');
    } else {
      view.dom.classList.remove('cm-fullscreen');
    }
  }

  // ── Autocomplete source ───────────────────────────────────────────────
  private autocompleteSource(context: CompletionContext): CompletionResult | null {
    // The completion catalog is the JS/Python API reference, which would offer
    // names a Rhai step cannot call (EHQ-76).
    if (this._options['mode'] === 'rhai') return null;

    // Check if the cursor is inside a function call — if so, try parameter hint completions first
    const paramResult = this.parameterHintCompletions(context);
    if (paramResult) return paramResult;

    // Grab everything that looks like identifiers and dots behind the cursor
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = line.text.slice(0, context.pos - line.from);

    // Find the token the user is currently typing: walk backward from cursor
    // collecting word chars, hyphens, underscores, and dots.
    let tokenStart = textBefore.length;
    while (tokenStart > 0 && /[\w.-]/.test(textBefore[tokenStart - 1])) {
      tokenStart--;
    }
    const fullToken = textBefore.slice(tokenStart);
    if (fullToken.length === 0) return null;

    // Split on the LAST dot to separate namespace from partial method name.
    // "demo_arrow.filt" → namespace="demo_arrow", partial="filt"
    // "demo_arrow."     → namespace="demo_arrow", partial=""
    // "demo"            → namespace=null,          partial="demo"
    const lastDot = fullToken.lastIndexOf('.');
    let namespace: string | null = null;
    let partial: string;
    let replaceFrom: number; // absolute position in document

    if (lastDot >= 0) {
      const beforeDot = fullToken.slice(0, lastDot);
      // Only treat as namespace.method if there's exactly one dot.
      // "demo_arrow.filt" → valid (one dot)
      // "demo_arrow.demo_arrow." → invalid (two dots, not a real namespace path)
      if (beforeDot.includes('.')) {
        return null;
      }
      namespace = beforeDot;
      partial = fullToken.slice(lastDot + 1);
      replaceFrom = line.from + tokenStart + lastDot + 1;
    } else {
      partial = fullToken;
      replaceFrom = line.from + tokenStart;
    }

    // Min length check: namespace or partial must be long enough
    const checkLen = namespace ? namespace.length : partial.length;
    if (checkLen < MIN_WORD_LENGTH_FOR_AUTOCOMPLETE) return null;

    // Determine language
    const mode = this._options['mode'];
    const language: 'python' | 'javascript' =
      mode === 'python' ? 'python' : 'javascript';

    // Build the query for the API reference service
    const query = namespace ? `${namespace}.${partial}` : partial;
    let completions = this.apiReferenceService.getCompletions(query, language);

    if (namespace) {
      completions = completions.filter(c => c.kind !== 'module');
    }

    if (completions.length === 0) return null;

    // Check what follows the cursor to avoid duplicating parens.
    // Handles both "save_file|()" and "save_file|)" scenarios.
    const textAfter = line.text.slice(context.pos - line.from);
    const trimmedAfter = textAfter.trimStart();
    const hasParensAfter = trimmedAfter.startsWith('(') || trimmedAfter.startsWith(')');

    return {
      from: replaceFrom,
      options: completions.map((item) => ({
        label: namespace ? item.text : item.fullName,
        type: item.kind === 'module' ? 'class' : 'function',
        detail: namespace ? (item.signature || '') : (item.description || ''),
        info: () => this.createDetailPanel(item),
        boost: item.score,
        apply: (view: EditorView, _completion: any, from: number, to: number) => {
          // When completing with a namespace, insert just the method name.
          // Without a namespace, insert the full dotted name.
          const baseName = namespace ? item.text : item.fullName;

          // Only add parens automatically for namespaced function completions.
          // Without a namespace, users may want a reference rather than a call.
          let insertText = baseName;
          let cursorOffset = baseName.length;
          if (namespace && item.kind === 'function' && !hasParensAfter) {
            insertText += '()';
            cursorOffset = baseName.length + 1; // between the parens
          } else if (
            namespace &&
            item.kind === 'function' &&
            hasParensAfter &&
            trimmedAfter.startsWith(')')
          ) {
            // Cursor is before a closing paren with no opening — add just the opening paren
            insertText += '(';
            cursorOffset = baseName.length + 1;
          } else if (namespace && item.kind === 'function' && hasParensAfter && trimmedAfter.startsWith('(')) {
            // Parens already follow the cursor — place cursor after the existing opening paren
            cursorOffset = baseName.length + 1;
          }

          view.dispatch({
            changes: { from, to, insert: insertText },
            selection: { anchor: from + cursorOffset },
          });
        },
      })),
      filter: false,
    };
  }

  // ── Parameter hint completions ──────────────────────────────────────
  /**
   * Detect if the cursor is inside a function call's argument list, determine
   * the parameter index, look up the paramHint, and return context-aware
   * completions (e.g. connection names filtered by type, enum values).
   */
  private parameterHintCompletions(context: CompletionContext): CompletionResult | null {
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = line.text.slice(0, context.pos - line.from);

    // Must be inside a string literal for parameter hint completions
    const stringStart = this.findStringStart(textBefore, context.pos - line.from);
    if (stringStart < 0) return null;

    // Strip the current (possibly unterminated) string so detectFunctionCall
    // only sees completed strings and correctly counts commas/parens.
    const textBeforeString = textBefore.slice(0, stringStart - 1);

    // Find the enclosing function call by scanning backward for an unmatched '('
    const callInfo = this.detectFunctionCall(textBeforeString);
    if (!callInfo) return null;

    const mode = this._options['mode'];
    const language: 'python' | 'javascript' = mode === 'python' ? 'python' : 'javascript';

    const hint = this.apiReferenceService.getParamHint(
      callInfo.namespace, callInfo.method, callInfo.paramIndex, language
    );
    if (!hint) return null;

    // Resolve the hint to a list of completion values
    const values = this.resolveParamHint(hint);
    if (values.length === 0) return null;

    const partial = textBefore.slice(stringStart);
    const from = line.from + stringStart;

    const filtered = partial
      ? values.filter(v => v.label.toLowerCase().includes(partial.toLowerCase()))
      : values;

    if (filtered.length === 0) return null;

    return {
      from,
      options: filtered.map(v => ({
        label: v.label,
        type: 'text',
        detail: v.detail || '',
        boost: v.label.toLowerCase().startsWith(partial.toLowerCase()) ? 10 : 0,
      })),
      filter: false,
    };
  }

  /**
   * Scan backward from cursor to detect namespace.method( call context.
   * Returns the namespace, method name, and 0-based parameter index, or null.
   * Correctly skips over string literal contents so commas inside strings
   * (e.g. `ehq.call("a,b", "method")`) don't inflate the parameter index.
   */
  private detectFunctionCall(textBefore: string): {
    namespace: string; method: string; paramIndex: number;
  } | null {
    let depth = 0;
    let paramIndex = 0;
    let parenPos = -1;

    // Walk backward to find the unmatched opening paren
    for (let i = textBefore.length - 1; i >= 0; i--) {
      const ch = textBefore[i];

      // Skip over string literals: when we hit a closing quote, jump backward
      // past the string contents to the matching opening quote.
      if (ch === '"' || ch === "'") {
        const quote = ch;
        i--;
        while (i >= 0) {
          if (textBefore[i] === quote) {
            // Check for escaped quote
            let backslashes = 0;
            for (let j = i - 1; j >= 0 && textBefore[j] === '\\'; j--) backslashes++;
            if (backslashes % 2 === 0) break; // unescaped — found the opening quote
          }
          i--;
        }
        continue; // skip the opening quote character
      }

      if (ch === ')') { depth++; }
      else if (ch === '(') {
        if (depth === 0) { parenPos = i; break; }
        depth--;
      } else if (ch === ',' && depth === 0) {
        paramIndex++;
      }
    }
    if (parenPos < 0) return null;

    // Extract the function name before the paren: namespace.method
    let nameEnd = parenPos;
    let nameStart = nameEnd;
    while (nameStart > 0 && /[\w.-]/.test(textBefore[nameStart - 1])) {
      nameStart--;
    }
    const funcToken = textBefore.slice(nameStart, nameEnd);
    const dotIdx = funcToken.indexOf('.');
    if (dotIdx < 0 || dotIdx !== funcToken.lastIndexOf('.')) return null;

    const namespace = funcToken.slice(0, dotIdx);
    const method = funcToken.slice(dotIdx + 1);
    if (!namespace || !method) return null;

    return { namespace, method, paramIndex };
  }

  /**
   * Find the start position of the string literal content the cursor is inside.
   * Returns the position after the opening quote, or -1 if not inside a string.
   */
  private findStringStart(textBefore: string, cursorCol: number): number {
    // Walk backward to find an unmatched opening quote
    for (let i = cursorCol - 1; i >= 0; i--) {
      const ch = textBefore[i];
      if (ch === '"' || ch === "'") {
        // Check it's not escaped
        let backslashes = 0;
        for (let j = i - 1; j >= 0 && textBefore[j] === '\\'; j--) backslashes++;
        if (backslashes % 2 === 0) {
          return i + 1; // Position after the opening quote
        }
      }
      // Stop at comma or opening paren — we've left the argument
      if (ch === ',' || ch === '(') return -1;
    }
    return -1;
  }

  /**
   * Resolve a ParamHint to a list of completion values.
   */
  private resolveParamHint(hint: ParamHint): { label: string; detail?: string }[] {
    switch (hint.kind) {
      case 'connection': {
        const connections = this.connectionService.connections();
        const typeFilter = hint.connectionTypes ?? [];
        return connections
          .filter(c => typeFilter.length === 0 || typeFilter.includes(c.type))
          .map(c => ({ label: c.name, detail: c.type }));
      }
      case 'file':
        return this.resolveFileHint();
      case 'step':
        return this.stepService.stepList.map(s => ({
          label: s.name,
          detail: s.type || `step ${s.sortOrder}`,
        }));
      case 'routine':
        return this.resolveRoutineHint();
      case 'stateKey':
        return this.resolveStateKeyHint();
      case 'llmModel':
        return this.resolveLlmModelHint();
      case 'pushDestination':
        return this.resolvePushDestinationHint();
      case 'enum':
        return (hint.values ?? []).map(v => ({ label: v }));
      default:
        return [];
    }
  }

  /**
   * Resolve pushDestination hint: enabled destinations that accept Manual
   * (script) notifications. Triggers async fetch on first access.
   */
  private resolvePushDestinationHint(): { label: string; detail?: string }[] {
    if (!this.pushDestinationsFetched) {
      const projectId = this.appStateService.projectId();
      if (projectId > 0) {
        this.pushDestinationsFetched = true;
        this.pushDestinationService.list(projectId).subscribe({
          next: dests => {
            this.cachedPushDestinations = dests
              .filter(d => d.enabled && d.events.includes('Manual'))
              .map(d => ({ label: d.name, detail: d.serviceType }));
          },
          error: () => {
            this.pushDestinationsFetched = false;
          },
        });
      }
    }
    return this.cachedPushDestinations;
  }

  /**
   * Resolve file hint using cached file names. Triggers async fetch on first access.
   */
  private resolveFileHint(): { label: string; detail?: string }[] {
    if (!this.fileNamesFetched) {
      this.fileNamesFetched = true;
      this.notebookFilesService.listFiles().then(names => {
        this.cachedFileNames = names;
      }).catch(() => {
        this.fileNamesFetched = false;
      });
    }
    return this.cachedFileNames.map(name => ({ label: name }));
  }

  /**
   * Resolve routine hint using cached routine names. Triggers async fetch on first access.
   */
  private resolveRoutineHint(): { label: string; detail?: string }[] {
    if (!this.routineNamesFetched) {
      this.routineNamesFetched = true;
      const projectId = this.appStateService.projectId();
      if (projectId) {
        this.executionPlanService.getPlans(projectId).subscribe({
          next: plans => {
            this.cachedRoutineNames = plans.map(p => p.name);
          },
          error: () => {
            this.routineNamesFetched = false;
          }
        });
      }
    }
    return this.cachedRoutineNames.map(name => ({ label: name }));
  }

  /**
   * Resolve stateKey hint using cached project state keys. Triggers async fetch on first access.
   */
  private resolveStateKeyHint(): { label: string; detail?: string }[] {
    if (!this.stateKeysFetched) {
      const projectId = this.appStateService.projectId();
      if (projectId > 0) {
        this.stateKeysFetched = true;
        this.projectStateService.listState({}).then(entries => {
          this.cachedStateKeys = entries.map(e => e.key);
        }).catch(() => { /* project state unavailable */ });
      }
    }
    return this.cachedStateKeys.map(key => ({ label: key, detail: 'state key' }));
  }

  /**
   * Resolve llmModel hint using cached LLM model names. Triggers async fetch on first access.
   */
  private resolveLlmModelHint(): { label: string; detail?: string }[] {
    if (!this.llmModelsFetched) {
      const projectId = this.appStateService.projectId();
      if (projectId) {
        this.llmModelsFetched = true;
        this.llmService.listModels(projectId).subscribe(models => {
          this.cachedLlmModels = models.map(m => ({
            label: m.modelName,
            detail: `${m.llmType} — ${m.displayName}`,
          }));
        });
      }
    }
    return this.cachedLlmModels;
  }

  // ── Detail panel for autocomplete docs ────────────────────────────────
  private createDetailPanel(item: AutocompleteItem): Node {
    if (!isPlatformBrowser(this.platformId)) {
      return document.createTextNode(item.fullName);
    }
    const div = document.createElement('div');
    div.className = 'cm-api-detail';
    div.innerHTML = `
      <div class="detail-header">${this.escapeHtml(item.fullName)}</div>
      <div class="detail-description">${this.escapeHtml(item.description || '')}</div>
      ${item.signature ? `<div class="detail-signature"><code>${this.escapeHtml(item.signature)}</code></div>` : ''}
      ${item.parameters?.length ? `
        <div class="detail-params">
          <div class="params-title">Parameters:</div>
          ${item.parameters.map(p =>
            `<div class="param-row">
              <span class="param-name">${this.escapeHtml(p.name)}</span>
              <span class="param-type">${this.escapeHtml(p.type)}</span>
              ${p.required ? '' : '<span class="param-optional">optional</span>'}
            </div>`
          ).join('')}
        </div>
      ` : ''}
      ${item.returnType ? `<div class="detail-returns">Returns: <code>${this.escapeHtml(item.returnType)}</code></div>` : ''}
      ${item.availability ? `<div class="detail-availability">Available: ${this.escapeHtml(item.availability)}</div>` : ''}
    `;
    return div;
  }

  // ── Hover tooltip for API method documentation ──────────────────────
  private apiHoverTooltip(view: EditorView, pos: number, _side: number): Tooltip | null {
    const line = view.state.doc.lineAt(pos);
    const text = line.text;
    const col = pos - line.from;

    // Expand from cursor position to find a namespace.method token
    // Include hyphens to match namespaces like "demo-arrow"
    let start = col;
    let end = col;
    while (start > 0 && /[\w.-]/.test(text[start - 1])) start--;
    while (end < text.length && /[\w.-]/.test(text[end])) end++;

    const token = text.slice(start, end);
    const dotIndex = token.indexOf('.');
    if (dotIndex < 0) return null;

    // Only handle single-dot tokens (namespace.method)
    if (token.indexOf('.', dotIndex + 1) >= 0) return null;

    const namespace = token.slice(0, dotIndex);
    const methodName = token.slice(dotIndex + 1);
    if (!namespace || !methodName) return null;

    const detail = this.apiReferenceService.getMethodDetail(namespace, methodName);
    if (!detail) return null;

    // Build an AutocompleteItem from the method detail for createDetailPanel
    const item: AutocompleteItem = {
      text: methodName,
      fullName: `${namespace}.${methodName}`,
      namespace,
      kind: 'function',
      description: detail.description,
      signature: detail.signature,
      returnType: detail.returnType,
      availability: detail.availability,
      isAsync: detail.isAsync,
      parameters: detail.parameters,
    };

    return {
      pos: line.from + start,
      end: line.from + end,
      above: true,
      create: () => ({
        dom: this.createDetailPanel(item) as HTMLElement,
      }),
    };
  }

  private escapeHtml(text: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      return text.replace(/[&<>"']/g, (ch) => {
        switch (ch) {
          case '&': return '&amp;';
          case '<': return '&lt;';
          case '>': return '&gt;';
          case '"': return '&quot;';
          case "'": return '&#39;';
          default: return ch;
        }
      });
    }
    const el = document.createElement('span');
    el.textContent = text;
    return el.innerHTML;
  }

  // ── Dynamic height ────────────────────────────────────────────────────
  private updateEditorHeight(): void {
    if (!this._view) return;

    const lineCount = this._view.state.doc.lines;
    const lineHeight = this._view.defaultLineHeight;
    const contentHeight = lineCount * lineHeight + EDITOR_PADDING;
    const minHeight = this.minimumHeight();
    const maxHeight = this.maximumHeight();
    let height = Math.max(contentHeight, minHeight);
    if (maxHeight > 0) {
      // Honor the documented minimum even if a caller passes a cap below it.
      height = Math.min(height, Math.max(maxHeight, minHeight));
    }

    this._view.dom.style.height = `${height}px`;
  }

  // ── Runtime option changes ────────────────────────────────────────────
  private applyOptionChanges(): void {
    if (!this._view) return;

    const effects: any[] = [];

    // Language change
    if (this._options['mode'] !== this._prevOptions['mode']) {
      effects.push(
        this.languageCompartment.reconfigure(
          this.getLanguageExtension(this._options['mode'])
        )
      );
      // Also update tab size for mode change
      const indentSize = this._options['mode'] === MODE_PYTHON ? PYTHON_INDENT_SIZE : DEFAULT_INDENT_SIZE;
      effects.push(
        this.tabSizeCompartment.reconfigure(EditorState.tabSize.of(indentSize))
      );
      // Swap the lint source to match the new language. (EHQ-108)
      effects.push(
        this.lintCompartment.reconfigure(this.getLintExtension(this._options['mode']))
      );
      // Clear the previous language's diagnostics. The squiggles live inside
      // lintCompartment and go away with the old linter, but lintGutter() is
      // installed outside it and its marker field only ever clears on a
      // setDiagnostics effect — so a mode with no linter (markdown, json, text)
      // would strand the old gutter icons, which then follow the text through
      // later edits. Clearing here also drops the stale markers during the
      // incoming linter's debounce. (EHQ-149)
      effects.push(setDiagnosticsEffect.of([]));
    }

    // Read-only change
    if (this._options['readOnly'] !== this._prevOptions['readOnly']) {
      effects.push(
        this.readOnlyCompartment.reconfigure(
          EditorState.readOnly.of(this._options['readOnly'] === true || this.disabled)
        )
      );
    }

    // Theme change
    if (this._options['theme'] !== this._prevOptions['theme']) {
      effects.push(
        this.themeCompartment.reconfigure(this.getThemeExtension(this._options['theme']))
      );
    }

    // Line numbers change
    if (this._options['lineNumbers'] !== this._prevOptions['lineNumbers']) {
      effects.push(
        this.lineNumbersCompartment.reconfigure(
          this._options['lineNumbers'] !== false ? lineNumbers() : []
        )
      );
    }

    // Line wrapping change
    if (this._options['lineWrapping'] !== this._prevOptions['lineWrapping']) {
      effects.push(
        this.lineWrappingCompartment.reconfigure(
          this._options['lineWrapping'] ? EditorView.lineWrapping : []
        )
      );
    }

    if (effects.length > 0) {
      this._view.dispatch({ effects });
    }
  }
}
